import { resolveRequestBodyRef } from './apiRenderingUtils';

/**
 * Converts form values to JSON structure based on OpenAPI schema
 *
 * @param {Object} values - Form values object where keys are field names (e.g., 'body_fieldName')
 * @param {Object} schema - OpenAPI schema object describing the request body structure
 * @param {Object} openApiSpec - Full OpenAPI specification for resolving references
 * @returns {Object} JSON object with properly structured request body data
 *
 * @example
 * const values = {
 *   body_name: 'John',
 *   body_address_city: 'NYC',
 *   body_tags: 'javascript,react'
 * };
 * const schema = {
 *   properties: {
 *     name: { type: 'string' },
 *     address: { type: 'object', properties: { city: { type: 'string' } } },
 *     tags: { type: 'array', items: { type: 'string' } }
 *   }
 * };
 * formValuesToJson(values, schema, openApiSpec);
 * // Returns: { name: 'John', address: { city: 'NYC' }, tags: ['javascript', 'react'] }
 */
export const formValuesToJson = (values, schema, openApiSpec) => {
  const result = {};
  if (!schema?.properties) return result;

  Object.entries(schema.properties).forEach(([key, property]) => {
    const resolvedProperty = resolveRequestBodyRef(property, openApiSpec);

    if (resolvedProperty?.type === 'object' && resolvedProperty?.properties) {
      // Handle nested object properties
      const nestedObj = {};
      Object.entries(resolvedProperty?.properties ?? {}).forEach(([nestedKey]) => {
        const formKey = `body_${key}_${nestedKey}`;
        const value = values[formKey];
        if (value !== undefined && value !== '') {
          nestedObj[nestedKey] = value;
        }
      });
      if (Object.keys(nestedObj).length > 0) {
        result[key] = nestedObj;
      }
    } else {
      // Handle primitive and array properties
      const value = values[`body_${key}`];
      if (value !== undefined && value !== '') {
        if (resolvedProperty?.type === 'array' && typeof value === 'string') {
          // Convert comma-separated string to array
          result[key] = value?.split(',')?.map((item) => item?.trim());
        } else {
          result[key] = value;
        }
      }
    }
  });

  return result;
};
